import { useState, useRef, useEffect } from 'react';
import { readStoredPanelPosition, storePanelPosition, fitExpandedPanelPosition, clampPanelPosition } from '../utils/panelPosition';

const useFloatingPanel = () => {
    const [collapsed, setCollapsed] = useState(false);

    const panelRef = useRef(null);
    const didDrag = useRef(false);
    const [position, setPosition] = useState(readStoredPanelPosition);
    const [dragging, setDragging] = useState(false);
    const hasCustomPosition = position !== null;

    const toggleCollapsed = () => {
        if (collapsed && position) {
            const fitted = fitExpandedPanelPosition(position);
            setPosition(fitted);
            storePanelPosition(fitted);
        }
        setCollapsed((previous) => !previous);
    };

    useEffect(() => {
        if (!hasCustomPosition) return;

        const keepVisible = () => {
            const rect = panelRef.current?.getBoundingClientRect();
            if (rect) setPosition((current) => (current ? clampPanelPosition(current, rect) : current));
        };

        keepVisible();
        window.addEventListener('resize', keepVisible);
        return () => window.removeEventListener('resize', keepVisible);
    }, [hasCustomPosition]);

    const onHeaderMouseDown = (e) => {
        if (e.button !== 0 || e.target.closest('.ai_chat_panel_header_actions')) return;
        e.preventDefault();

        const rect = panelRef.current.getBoundingClientRect();
        const start = { x: e.clientX, y: e.clientY, left: rect.left, top: rect.top };
        let latest = null;

        const onMouseMove = (moveEvent) => {
            if (latest === null && Math.hypot(moveEvent.clientX - start.x, moveEvent.clientY - start.y) < 4) return;
            setDragging(true);
            latest = clampPanelPosition({
                left: start.left + moveEvent.clientX - start.x,
                top: start.top + moveEvent.clientY - start.y,
            }, rect);
            setPosition(latest);
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            if (latest === null) return;

            setDragging(false);
            storePanelPosition(latest);
            didDrag.current = true;
            setTimeout(() => { didDrag.current = false; }, 0);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const onHeaderClick = () => {
        if (didDrag.current) return;
        toggleCollapsed();
    };

    return { panelRef, collapsed, toggleCollapsed, position, hasCustomPosition, dragging, onHeaderMouseDown, onHeaderClick };
};

export default useFloatingPanel;
